// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import {
  postAdminContentGenerateText,
  postAdminContentGenerateImage,
  postAdminContentEditImage,
  postAdminContentRewriteTone,
  postAdminContentShorten,
  postAdminContentRefine,
  postAdminContentSuggestTopics,
  postAdminContentGenerateImagePrompt,
  postAdminContentGenerateHashtags,
  postAdminContentSeoEnrich,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

export function createContentNamespace(rb: RequestBuilder) {
  return {
    /**
     * Generate text from a prompt, optionally informed by brand identity.
     * @param attributes - { prompt, workspace_id, brand_identity_id?, task? }
     * @returns { text: string }
     * @example
     * ```typescript
     * const result = await admin.content.generateText({
     *   prompt: "Write about AI analytics",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    generateText: async (
      attributes: {
        prompt: string;
        workspace_id: string;
        brand_identity_id?: string;
        task?: string;
      },
      options?: RequestOptions,
    ) => {
      const { prompt, workspace_id, brand_identity_id, task } = attributes;
      return rb.execute(
        postAdminContentGenerateText,
        {
          body: {
            data: { prompt, workspace_id, brand_identity_id, task },
          },
        },
        options,
      );
    },

    /**
     * Generate an image from a text prompt via Gemini.
     * @param attributes - { prompt, workspace_id, style? }
     * @returns { url: string, provider: string }
     * @example
     * ```typescript
     * const result = await admin.content.generateImage({
     *   prompt: "A futuristic city skyline",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    generateImage: async (
      attributes: {
        prompt: string;
        workspace_id: string;
        style?: string;
      },
      options?: RequestOptions,
    ) => {
      const { prompt, workspace_id, style } = attributes;
      return rb.execute(
        postAdminContentGenerateImage,
        {
          body: {
            data: { prompt, workspace_id, style },
          },
        },
        options,
      );
    },

    /**
     * Edit an existing image using natural language instructions.
     * @param attributes - { image_url, instructions, workspace_id }
     * @returns { url: string, provider: string }
     * @example
     * ```typescript
     * const result = await admin.content.editImage({
     *   image_url: "https://example.com/photo.png",
     *   instructions: "Remove the background",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    editImage: async (
      attributes: {
        image_url: string;
        instructions: string;
        workspace_id: string;
      },
      options?: RequestOptions,
    ) => {
      const { image_url, instructions, workspace_id } = attributes;
      return rb.execute(
        postAdminContentEditImage,
        {
          body: {
            data: { image_url, instructions, workspace_id },
          },
        },
        options,
      );
    },

    /**
     * Rewrite text in a different tone.
     * @param attributes - { text, target_tone, workspace_id, max_length? }
     * @returns { rewritten_text: string }
     * @example
     * ```typescript
     * const result = await admin.content.rewriteTone({
     *   text: "We are pleased to announce our new product.",
     *   target_tone: "casual",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    rewriteTone: async (
      attributes: {
        text: string;
        target_tone: string;
        workspace_id: string;
        max_length?: number;
      },
      options?: RequestOptions,
    ) => {
      const { text, target_tone, workspace_id, max_length } = attributes;
      return rb.execute(
        postAdminContentRewriteTone,
        {
          body: {
            data: { text, target_tone, workspace_id, max_length },
          },
        },
        options,
      );
    },

    /**
     * Shorten text to a target character count.
     * @param attributes - { text, max_chars, workspace_id, platform? }
     * @returns { shortened_text: string }
     * @example
     * ```typescript
     * const result = await admin.content.shorten({
     *   text: "This is a long piece of content that needs shortening.",
     *   max_chars: 280,
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    shorten: async (
      attributes: {
        text: string;
        max_chars: number;
        workspace_id: string;
        platform?: string;
      },
      options?: RequestOptions,
    ) => {
      const { text, max_chars, workspace_id, platform } = attributes;
      return rb.execute(
        postAdminContentShorten,
        {
          body: {
            data: { text, max_chars, workspace_id, platform },
          },
        },
        options,
      );
    },

    /**
     * Refine text based on freeform instructions.
     * @param attributes - { text, instructions, workspace_id }
     * @returns { refined_text: string }
     * @example
     * ```typescript
     * const result = await admin.content.refine({
     *   text: "Draft blog post content here.",
     *   instructions: "Make it more engaging and add a call to action",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    refine: async (
      attributes: {
        text: string;
        instructions: string;
        workspace_id: string;
      },
      options?: RequestOptions,
    ) => {
      const { text, instructions, workspace_id } = attributes;
      return rb.execute(
        postAdminContentRefine,
        {
          body: {
            data: { text, instructions, workspace_id },
          },
        },
        options,
      );
    },

    /**
     * Suggest content topics based on industry and audience.
     * @param attributes - { workspace_id, industry?, brand_identity_id?, count? }
     * @returns { suggestions: string }
     * @example
     * ```typescript
     * const result = await admin.content.suggestTopics({
     *   workspace_id: "ws_abc",
     *   industry: "fintech",
     *   count: 5,
     * });
     * ```
     */
    suggestTopics: async (
      attributes: {
        workspace_id: string;
        industry?: string;
        brand_identity_id?: string;
        count?: number;
      },
      options?: RequestOptions,
    ) => {
      const { workspace_id, industry, brand_identity_id, count } = attributes;
      return rb.execute(
        postAdminContentSuggestTopics,
        {
          body: {
            data: { workspace_id, industry, brand_identity_id, count },
          },
        },
        options,
      );
    },

    /**
     * Generate an image generation prompt from marketing copy.
     * @param attributes - { marketing_copy, workspace_id, style? }
     * @returns { image_prompt: string }
     * @example
     * ```typescript
     * const result = await admin.content.generateImagePrompt({
     *   marketing_copy: "Launch your brand into the future",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    generateImagePrompt: async (
      attributes: {
        marketing_copy: string;
        workspace_id: string;
        style?: string;
      },
      options?: RequestOptions,
    ) => {
      const { marketing_copy, workspace_id, style } = attributes;
      return rb.execute(
        postAdminContentGenerateImagePrompt,
        {
          body: {
            data: { marketing_copy, workspace_id, style },
          },
        },
        options,
      );
    },

    /**
     * Generate scored hashtags for content.
     * @param attributes - { text, platform, workspace_id, industry?, count? }
     * @returns { hashtags: Array<{ tag: string, score: number }> }
     * @example
     * ```typescript
     * const result = await admin.content.generateHashtags({
     *   text: "Check out our new AI-powered analytics tool",
     *   platform: "instagram",
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    generateHashtags: async (
      attributes: {
        text: string;
        platform: string;
        workspace_id: string;
        industry?: string;
        count?: number;
      },
      options?: RequestOptions,
    ) => {
      const { text, platform, workspace_id, industry, count } = attributes;
      return rb.execute(
        postAdminContentGenerateHashtags,
        {
          body: {
            data: { text, platform, workspace_id, industry, count },
          },
        },
        options,
      );
    },

    /**
     * Analyze content for SEO optimization against target keywords.
     * @param attributes - { text, target_keywords, workspace_id }
     * @returns { analysis: { keyword_coverage, suggestions, seo_score } }
     * @example
     * ```typescript
     * const result = await admin.content.seoEnrich({
     *   text: "Our product helps businesses grow",
     *   target_keywords: ["growth", "business", "scale"],
     *   workspace_id: "ws_abc",
     * });
     * ```
     */
    seoEnrich: async (
      attributes: {
        text: string;
        target_keywords: string[];
        workspace_id: string;
      },
      options?: RequestOptions,
    ) => {
      const { text, target_keywords, workspace_id } = attributes;
      return rb.execute(
        postAdminContentSeoEnrich,
        {
          body: {
            data: { text, target_keywords, workspace_id },
          },
        },
        options,
      );
    },
  };
}
